import React, { type ReactElement, type ReactNode, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew'
import Person from '@mui/icons-material/Person'
import type { SxProps, Theme } from '@mui/material/styles'

import { SidebarMenuItem, type SidebarMenuItemProps } from './SidebarMenuItem'
import { useMobile } from '../hooks/useMobile'

const styles = {
    open: {
        transition: (theme: Theme) =>
            theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
    },
    close: {
        transition: (theme: Theme) =>
            theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        width: 0,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        '& .MuiDivider-root': {
            my: 0.5,
        },
    },
    list: {
        overflowY: 'auto',
        flex: 1,
    },
}

export interface SidebarLayoutProps {
    /** Drawer width (default: 240) */
    width?: number
    /** Controls whether the drawer is open */
    open?: boolean
    /** Called when the drawer should open (for mobile swipe) */
    onOpen?: () => void
    /** Called when the drawer should close (for mobile swipe) */
    onClose?: () => void
    /** Use SwipeableDrawer on mobile instead of persistent Drawer */
    mobile?: boolean
    /** Content for the logo/branding area at the top */
    header: ReactNode
    /** Optional content between header and nav list (e.g. GlobalCreate) */
    headerExtra?: ReactNode
    /** Menu items rendered via SidebarMenuItem */
    menuItems: SidebarMenuItemProps[]
    /** Optional content between nav list and user section (e.g. status badge, device info) */
    footer?: ReactNode
    /** User section */
    user?: {
        name: string
        profileUrl?: string
        secondaryText?: SidebarMenuItemProps['secondaryText']
        onLogout: () => void
        alternateAction?: { label: string; icon?: ReactElement; onClick: () => void }
        /** When true, the profile item never shows the active highlight (e.g. when profileUrl overlaps a nav item) */
        disableProfileHighlight?: boolean
    }
    /** Version display at the very bottom */
    version?: ReactNode
    /** Additional sx applied to the outer nav Box */
    sx?: SxProps<Theme>
}

export const SidebarLayout = ({
    width = 240,
    open = true,
    onOpen,
    onClose,
    mobile,
    header,
    headerExtra,
    menuItems,
    footer,
    user,
    version,
    sx,
}: SidebarLayoutProps) => {
    const { isMobile: isMobileDetected } = useMobile()
    const isMobile = mobile ?? isMobileDetected

    // Close drawer on navigation when in mobile mode
    const { pathname } = useLocation()
    const prevPathnameRef = useRef(pathname)
    useEffect(() => {
        if (prevPathnameRef.current !== pathname) {
            prevPathnameRef.current = pathname
            if (isMobile) {
                onClose?.()
            }
        }
    }, [pathname, isMobile, onClose])

    const menu = (
        <Box
            component="nav"
            id="sidebar"
            data-test-isopen={open}
            sx={[styles.container, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        >
            {header}
            {headerExtra}
            <Divider />
            <List id="menu" disablePadding sx={styles.list}>
                {menuItems.map((item, ind) => (
                    <React.Fragment key={item.url || ind}>
                        <SidebarMenuItem {...item} />
                        {item.divider && <Divider />}
                    </React.Fragment>
                ))}
            </List>
            <Divider />
            {footer}
            {user && (
                <>
                    {footer && <Divider />}
                    <SidebarMenuItem
                        name={user.name}
                        icon={<Person />}
                        url={user.profileUrl}
                        id="profile"
                        secondaryText={user.secondaryText}
                        disableActiveHighlight={user.disableProfileHighlight}
                    />
                    <Divider />
                    {user.alternateAction ? (
                        <SidebarMenuItem
                            name={user.alternateAction.label}
                            icon={user.alternateAction.icon}
                            id="stop-impersonation"
                            onClick={user.alternateAction.onClick}
                        />
                    ) : (
                        <SidebarMenuItem
                            name="Log out"
                            icon={<PowerSettingsNew />}
                            id="logout-button"
                            onClick={user.onLogout}
                        />
                    )}
                </>
            )}
            {version}
        </Box>
    )

    if (isMobile) {
        const handleOpen = onOpen ?? (() => undefined)
        const handleClose = onClose ?? (() => undefined)
        return (
            <SwipeableDrawer
                anchor="left"
                disableSwipeToOpen
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                sx={{ visibility: open !== true ? 'hidden' : undefined }}
            >
                {menu}
            </SwipeableDrawer>
        )
    }

    return (
        <Drawer
            variant="persistent"
            anchor="left"
            open={open !== false}
            sx={{
                flexShrink: 0,
                ...(open !== false ? { ...styles.open, width } : styles.close),
                '& .MuiDrawer-paper': {
                    width,
                    boxSizing: 'border-box',
                },
            }}
        >
            {menu}
        </Drawer>
    )
}
